import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { computeTotals, type LineItem, type Totals } from '@/types/sales';
import { formatCurrency } from '@/lib/utils';
import { useMemo } from 'react';

type Props = {
  lineItems: LineItem[];
  discountPct?: number;
  taxRatePct?: number;
  shipping?: number;
  onDiscountChange?: (v: number) => void;
  onTaxRateChange?: (v: number) => void;
  onShippingChange?: (v: number) => void;
  showMargin?: boolean;
  readonly?: boolean;
};

export function TotalsPanel({
  lineItems, discountPct, taxRatePct, shipping,
  onDiscountChange, onTaxRateChange, onShippingChange,
  showMargin, readonly,
}: Props) {
  const totals: Totals = useMemo(
    () => computeTotals(lineItems, { discountPct, taxRatePct, shipping }),
    [lineItems, discountPct, taxRatePct, shipping],
  );

  return (
    <Card>
      <div className="kicker mb-4">Totals</div>

      {/* Inputs (when editable) */}
      {!readonly && (
        <div className="grid grid-cols-3 gap-3 mb-5 pb-5 border-b border-border">
          <Input
            label="Discount %"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={discountPct !== undefined ? discountPct * 100 : ''}
            onChange={(e) => onDiscountChange?.(e.target.value === '' ? 0 : Number(e.target.value) / 100)}
            placeholder="0"
          />
          <Input
            label="Tax Rate %"
            type="number"
            min="0"
            max="100"
            step="0.001"
            value={taxRatePct !== undefined ? taxRatePct * 100 : ''}
            onChange={(e) => onTaxRateChange?.(e.target.value === '' ? 0 : Number(e.target.value) / 100)}
            placeholder="9.5"
            hint="e.g. 9.5 for LA County"
          />
          <Input
            label="Shipping"
            type="number"
            min="0"
            step="0.01"
            value={shipping ?? ''}
            onChange={(e) => onShippingChange?.(e.target.value === '' ? 0 : Number(e.target.value))}
            placeholder="0.00"
          />
        </div>
      )}

      {/* Totals breakdown */}
      <dl className="space-y-2 text-sm">
        <Row label="Subtotal" value={formatCurrency(totals.subtotal)} />
        {totals.discount > 0 && (
          <Row label={`Discount${discountPct ? ` (${(discountPct * 100).toFixed(1)}%)` : ''}`}
               value={`− ${formatCurrency(totals.discount)}`} muted />
        )}
        {totals.tax > 0 && (
          <Row label={`Tax (${(totals.taxRate * 100).toFixed(2)}%)`}
               value={formatCurrency(totals.tax)} muted />
        )}
        {totals.shipping > 0 && (
          <Row label="Shipping" value={formatCurrency(totals.shipping)} muted />
        )}

        <div className="pt-3 mt-3 border-t border-border-strong flex items-baseline justify-between">
          <dt className="kicker">Grand Total</dt>
          <dd className="font-display text-3xl text-brass-300 tabular leading-none">
            {formatCurrency(totals.total)}
          </dd>
        </div>

        {showMargin && totals.cost !== undefined && totals.margin !== undefined && (
          <div className="pt-4 mt-4 border-t border-border space-y-2">
            <Row label="Total Cost" value={formatCurrency(totals.cost)} muted />
            <div className="flex items-baseline justify-between">
              <dt className="text-xs text-text-dim uppercase tracking-ultra">Margin</dt>
              <dd>
                <Badge tone={totals.margin >= 0.3 ? 'success' : totals.margin >= 0.15 ? 'warning' : 'danger'}>
                  {(totals.margin * 100).toFixed(1)}%
                </Badge>
              </dd>
            </div>
          </div>
        )}
      </dl>
    </Card>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className={muted ? 'text-text-dim' : 'text-text-muted'}>{label}</dt>
      <dd className={`tabular ${muted ? 'text-text-muted' : 'text-text'}`}>{value}</dd>
    </div>
  );
}
